import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { Icon } from '../../../pages/DesignSystem';
import toast from 'react-hot-toast';
import { Event } from '../../../types/event.types';
import type { EventMetrics, SeriesMetrics } from '../../../types/organizer-events.types';
import EventCardMenu from '../EventList/EventCardMenu';
import { supabaseEventService } from '../../../supabase/event.service';
import { smartListenerService } from '../../../supabase/SmartListenerService';
import './PlanningWeekGrid.css';

const DAY_NAMES_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOUR_HEIGHT = 60; // pixels per hour with events
const EMPTY_HOUR_HEIGHT = 30; // pixels per hour without events (half height)

/** Default visible range — hours outside this are collapsed by default */
const CORE_START = 9;
const CORE_END = 21;
const PADDING_HOURS = 1;

interface PlanningWeekGridProps {
  weekStart: Date;
  eventsByDay: Map<string, Event[]>;
  selectedDateKey: string;
  eventMetrics: Record<string, EventMetrics>;
  seriesMetrics: Record<string, SeriesMetrics>;
  metricsLoading: boolean;
  onEdit: (event: Event) => void;
  onDuplicate: (event: Event) => void;
  onCancel?: (event: Event) => void;
  onRestore?: (event: Event) => void;
  onPublish?: (event: Event) => void;
  onDelete?: (event: Event) => void;
  onInstagram?: (event: Event) => void;
  onAddOccurrence?: (event: Event) => void;
  onCreateEvent: () => void;
  onCreateEventAt?: (datetime: Date) => void;
  onMoveEvent?: (eventId: string, newDatetime: Date, originalEvent: Event) => void;
}

const toDayKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isToday = (date: Date): boolean => {
  const now = new Date();
  return date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
};

/** Extract hours/minutes in a specific timezone using Intl */
const getPartsInTz = (d: Date, tz?: string): { hour: number; minute: number } => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric', minute: 'numeric', hour12: false,
      timeZone: tz || undefined,
    }).formatToParts(d);
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
    return { hour, minute };
  } catch {
    return { hour: d.getHours(), minute: d.getMinutes() };
  }
};

const getDecimalHour = (d: Date, tz?: string): number => {
  const { hour, minute } = getPartsInTz(d, tz);
  return hour + minute / 60;
};

const parseEndTime = (endTime: string, tz?: string): number => {
  if (endTime.includes('T')) {
    const d = new Date(endTime);
    return getDecimalHour(d, tz);
  }
  const [h, m] = endTime.split(':').map(Number);
  return h + (m || 0) / 60;
};

/** Detect if user's locale uses 12h format */
const is12h = (() => {
  try {
    const options = new Intl.DateTimeFormat(navigator.language, { hour: 'numeric' }).resolvedOptions();
    return options.hour12 === true;
  } catch { return false; }
})();

const formatTime = (decimalHour: number): string => {
  const h = Math.floor(decimalHour);
  const m = Math.round((decimalHour - h) * 60);
  if (is12h) {
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return m > 0 ? `${h12}:${String(m).padStart(2, '0')} ${period}` : `${h12} ${period}`;
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

interface PositionedEvent {
  event: Event;
  top: number;
  height: number;
  startHour: number;
  endHour: number;
  /** Stack depth: 0 = bottom (longest), higher = on top (shortest) */
  layer: number;
  /** How many events overlap in this group */
  overlapCount: number;
  /** Pixels of this event's top area covered by events above it */
  coveredHeight: number;
}

const STACK_OFFSET = 10; // px left offset per stacked layer
const STACK_RIGHT_INSET = 8; // px right inset for foreground events (peek)

/**
 * Assign stacking layers to overlapping events.
 * Longest events go to bottom (layer 0), shortest on top.
 * Computes coveredHeight for background events to offset their text.
 */
const layoutOverlaps = (events: PositionedEvent[]): PositionedEvent[] => {
  if (events.length <= 1) return events;

  // Find overlap groups first (sorted by start time)
  const sorted = [...events].sort((a, b) => a.startHour - b.startHour);

  const groups: PositionedEvent[][] = [];
  let currentGroup: PositionedEvent[] = [sorted[0]];
  let groupEnd = sorted[0].endHour;

  for (let i = 1; i < sorted.length; i++) {
    const ev = sorted[i];
    if (ev.startHour < groupEnd) {
      currentGroup.push(ev);
      groupEnd = Math.max(groupEnd, ev.endHour);
    } else {
      groups.push(currentGroup);
      currentGroup = [ev];
      groupEnd = ev.endHour;
    }
  }
  groups.push(currentGroup);

  // Within each group: sort by duration DESC (longest = layer 0 = bottom)
  for (const group of groups) {
    const count = group.length;
    group.sort((a, b) => {
      const durA = a.endHour - a.startHour;
      const durB = b.endHour - b.startHour;
      if (durA !== durB) return durB - durA; // longest first
      return a.startHour - b.startHour; // then earliest
    });

    for (let i = 0; i < count; i++) {
      group[i].layer = i;
      group[i].overlapCount = count;
    }

    // Compute coveredHeight for the background event (layer 0)
    // = how far down from its top the foreground events extend
    if (count > 1) {
      const bg = group[0]; // background = longest, layer 0
      let maxForegroundBottom = bg.top;
      for (let j = 1; j < count; j++) {
        const fg = group[j];
        const fgBottom = fg.top + fg.height;
        maxForegroundBottom = Math.max(maxForegroundBottom, fgBottom);
      }
      bg.coveredHeight = Math.max(0, maxForegroundBottom - bg.top);
    }
  }

  return events;
};

const PlanningWeekGrid: React.FC<PlanningWeekGridProps> = ({
  weekStart,
  eventsByDay,
  selectedDateKey,
  eventMetrics,
  seriesMetrics,
  metricsLoading,
  onEdit,
  onDuplicate,
  onCancel,
  onRestore,
  onPublish,
  onDelete,
  onInstagram,
  onAddOccurrence,
  onCreateEventAt,
  onMoveEvent,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);

  // Collapsible state for early morning / late evening
  const [showEarlyHours, setShowEarlyHours] = useState(false);
  const [showLateHours, setShowLateHours] = useState(false);
  // Track which event was last hovered — stays on top of its overlap group
  const [topEventId, setTopEventId] = useState<string | null>(null);

  // Click-to-create: placeholder on empty slot
  const [placeholder, setPlaceholder] = useState<{ dayKey: string; hour: number; top: number; height: number } | null>(null);

  // Hover hint "+" — which hour slot is hovered
  const [hoverHint, setHoverHint] = useState<{ dayKey: string; top: number; height: number } | null>(null);

  // Popover state — which event is being previewed
  const [popover, setPopover] = useState<{ event: Event; x: number; y: number; impressions: number } | null>(null);
  const popoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track if we just finished a drag (to prevent click from opening edit)
  const justDraggedRef = useRef(false);

  // Drag-and-drop state
  interface DragState {
    event: Event;
    startX: number;
    startY: number;
    isDragging: boolean; // true once mouse moved > threshold
    ghostX: number;
    ghostY: number;
    targetDayKey: string | null;
    targetHour: number;
  }
  const [dragState, setDragState] = useState<DragState | null>(null);
  const dragThreshold = 5; // px before drag starts
  const columnsRef = useRef<Map<string, HTMLDivElement>>(new Map());

  // Resize state
  interface ResizeState {
    event: Event;
    startY: number;
    isResizing: boolean;
    originalEndHour: number;
    newEndHour: number;
    columnKey: string;
  }
  const [resizeState, setResizeState] = useState<ResizeState | null>(null);

  // Compute: absolute min/max from events, and the "smart" visible range
  const { eventMinHour, eventMaxHour, columns } = useMemo(() => {
    let minH = 24;
    let maxH = 0;

    const cols: { date: Date; key: string; dayName: string; dayNum: number; isToday: boolean; isSelected: boolean; events: Event[] }[] = [];

    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      const key = toDayKey(d);
      const dayEvents = eventsByDay.get(key) || [];
      cols.push({
        date: d,
        key,
        dayName: DAY_NAMES_SHORT[i],
        dayNum: d.getDate(),
        isToday: isToday(d),
        isSelected: key === selectedDateKey,
        events: dayEvents,
      });

      for (const ev of dayEvents) {
        if (ev.datetime) {
          const start = getDecimalHour(new Date(ev.datetime), ev.timezone);
          minH = Math.min(minH, start);
          let end = start + 1;
          if (ev.endTime) {
            end = parseEndTime(ev.endTime, ev.timezone);
            if (end <= start) end = start + 1;
          }
          maxH = Math.max(maxH, end);
        }
      }
    }

    // If no events found, use core range
    if (minH >= 24) { minH = CORE_START; maxH = CORE_END; }

    return {
      absoluteStart: Math.max(0, Math.floor(minH) - PADDING_HOURS),
      absoluteEnd: Math.min(24, Math.ceil(maxH) + PADDING_HOURS),
      eventMinHour: minH,
      eventMaxHour: maxH,
      columns: cols,
    };
  }, [weekStart, eventsByDay, selectedDateKey]);

  // Compute the visible range based on collapsed state
  // - Default: show from max(eventMin, CORE_START) to min(eventMax, CORE_END)
  //   but always at least CORE_START..CORE_END if events are within core
  // - If events are before CORE_START, extend down to include them
  // - If events are after CORE_END, extend up to include them
  // - "Show earlier/later" expands to absoluteStart/absoluteEnd
  const hasEarlyEvents = eventMinHour < CORE_START;
  const hasLateEvents = eventMaxHour > CORE_END;

  // The "smart" start: if events before 9am, start at the event's hour (no extra padding)
  const smartStart = hasEarlyEvents ? Math.max(0, Math.floor(eventMinHour)) : CORE_START;
  // The "smart" end: if events after 9pm, end at the event's end hour (no extra padding)
  const smartEnd = hasLateEvents ? Math.min(24, Math.ceil(eventMaxHour)) : CORE_END;

  // When expanded: go all the way to 0 or 24
  const visibleStart = showEarlyHours ? 0 : smartStart;
  const visibleEnd = showLateHours ? 24 : smartEnd;

  const earlyHoursHidden = visibleStart > 0 && !showEarlyHours;
  const lateHoursHidden = visibleEnd < 24 && !showLateHours;

  // Determine which hours have events (across all days) for variable height
  const { hourOffsets, hourHeights, gridHeight, hourLabels } = useMemo(() => {
    const active = new Set<number>();
    for (const col of columns) {
      for (const ev of col.events) {
        if (!ev.datetime) continue;
        const evStart = getDecimalHour(new Date(ev.datetime), ev.timezone);
        let evEnd = evStart + 1;
        if (ev.endTime) {
          evEnd = parseEndTime(ev.endTime, ev.timezone);
          if (evEnd <= evStart) evEnd = evStart + 1;
        }
        // Mark each hour the event touches
        for (let h = Math.floor(evStart); h < Math.ceil(evEnd); h++) {
          active.add(h);
        }
      }
    }

    // Build offset map
    const offsets: Record<number, number> = {};
    const heights: Record<number, number> = {};
    const labels: number[] = [];
    let offset = 0;

    for (let h = visibleStart; h < visibleEnd; h++) {
      labels.push(h);
      offsets[h] = offset;
      const height = active.has(h) ? HOUR_HEIGHT : EMPTY_HOUR_HEIGHT;
      heights[h] = height;
      offset += height;
    }

    return { activeHours: active, hourOffsets: offsets, hourHeights: heights, gridHeight: offset, hourLabels: labels };
  }, [columns, visibleStart, visibleEnd]);

  /** Convert a decimal hour to a Y pixel offset using variable heights */
  const hourToY = useCallback((decimalHour: number): number => {
    const wholeHour = Math.floor(decimalHour);
    const fraction = decimalHour - wholeHour;
    const baseOffset = hourOffsets[wholeHour] ?? 0;
    const hHeight = hourHeights[wholeHour] ?? HOUR_HEIGHT;
    return baseOffset + fraction * hHeight;
  }, [hourOffsets, hourHeights]);

  // Auto-scroll to first event on mount / when week changes
  useEffect(() => {
    if (bodyRef.current && eventMinHour >= visibleStart + 2) {
      const scrollTo = hourToY(eventMinHour) - HOUR_HEIGHT;
      bodyRef.current.scrollTop = Math.max(0, scrollTo);
    }
  }, [eventMinHour, visibleStart, hourToY]);

  // Reset state when week changes
  useEffect(() => {
    setShowEarlyHours(false);
    setShowLateHours(false);
    setPlaceholder(null);
  }, [weekStart]);

  // Escape key dismisses placeholder
  useEffect(() => {
    if (!placeholder) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPlaceholder(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [placeholder]);

  const positionEvents = useCallback((events: Event[]): PositionedEvent[] => {
    const raw: PositionedEvent[] = events
      .filter(ev => ev.datetime)
      .map(ev => {
        const evStart = getDecimalHour(new Date(ev.datetime!), ev.timezone);
        let evEnd = evStart + 1;
        if (ev.endTime) {
          evEnd = parseEndTime(ev.endTime, ev.timezone);
          if (evEnd <= evStart) evEnd = evStart + 1;
        }
        const top = hourToY(evStart);
        const bottom = hourToY(evEnd);
        const height = Math.max(bottom - top, 28);
        return { event: ev, top, height, startHour: evStart, endHour: evEnd, layer: 0, overlapCount: 1, coveredHeight: 0 };
      });

    return layoutOverlaps(raw);
  }, [hourToY]);

  const getActiveTab = (ev: Event): 'active' | 'draft' | 'past' => {
    if (ev.status === 'draft') return 'draft';
    if (ev.status === 'cancelled') return 'active';
    if (ev.datetime && new Date(ev.datetime) < new Date()) return 'past';
    return 'active';
  };

  /** Convert a Y pixel offset back to a decimal hour (snap to 30min) */
  const yToHour = useCallback((y: number): number => {
    // Walk through hour offsets to find which hour this Y falls in
    for (let i = hourLabels.length - 1; i >= 0; i--) {
      const h = hourLabels[i];
      if (y >= hourOffsets[h]) {
        const fraction = (y - hourOffsets[h]) / hourHeights[h];
        const decimal = h + fraction;
        // Snap to 30min
        return Math.round(decimal * 2) / 2;
      }
    }
    return visibleStart;
  }, [hourLabels, hourOffsets, hourHeights, visibleStart]);

  /** Show popover on event hover after 300ms delay */
  const handleEventMouseEnter = useCallback((e: React.MouseEvent, event: Event, impressions: number) => {
    if (dragState?.isDragging) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // Position popover to the right of the event
    const x = rect.right + 8;
    const y = rect.top;
    popoverTimerRef.current = setTimeout(() => {
      setPopover({ event, x, y, impressions });
    }, 300);
  }, [dragState]);

  const handleEventMouseLeave = useCallback(() => {
    if (popoverTimerRef.current) {
      clearTimeout(popoverTimerRef.current);
      popoverTimerRef.current = null;
    }
    setPopover(null);
  }, []);

  /** Track which hour slot is hovered for "+" hint */
  const handleColumnMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>, col: typeof columns[0]) => {
    if ((e.target as HTMLElement).closest('.planning-week-grid__event')) {
      setHoverHint(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const hour = yToHour(y);
    const wholeHour = Math.floor(hour);
    const slotTop = hourOffsets[wholeHour] ?? 0;
    const slotHeight = hourHeights[wholeHour] ?? HOUR_HEIGHT;
    setHoverHint({ dayKey: col.key, top: slotTop, height: slotHeight });
  }, [yToHour, hourOffsets, hourHeights]);

  const handleColumnMouseLeave = useCallback(() => {
    setHoverHint(null);
  }, []);

  /** Handle click on a day column to create an event */
  const handleColumnClick = useCallback((e: React.MouseEvent<HTMLDivElement>, col: typeof columns[0]) => {
    // Don't trigger if clicking on an event
    if ((e.target as HTMLElement).closest('.planning-week-grid__event')) return;
    if (!onCreateEventAt) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const hour = yToHour(y);

    // Show placeholder
    const placeholderTop = hourToY(hour);
    const placeholderHeight = hourToY(hour + 1) - placeholderTop;
    setPlaceholder({ dayKey: col.key, hour, top: placeholderTop, height: Math.max(placeholderHeight, 30) });
  }, [yToHour, hourToY, onCreateEventAt]);

  /** Confirm creation from placeholder */
  const handlePlaceholderClick = useCallback(() => {
    if (!placeholder || !onCreateEventAt) return;
    const date = new Date(placeholder.dayKey + 'T00:00:00');
    const hours = Math.floor(placeholder.hour);
    const minutes = Math.round((placeholder.hour - hours) * 60);
    date.setHours(hours, minutes, 0, 0);
    setPlaceholder(null);
    onCreateEventAt(date);
  }, [placeholder, onCreateEventAt]);

  /** Dismiss placeholder */
  const handleDismissPlaceholder = useCallback(() => {
    setPlaceholder(null);
  }, []);

  // === Drag-and-drop handlers ===

  const handleEventPointerDown = useCallback((e: React.PointerEvent, event: Event) => {
    // Don't drag cancelled events or if no move handler
    if (!onMoveEvent || event.status === 'cancelled') return;
    // Don't start drag from menu/buttons
    if ((e.target as HTMLElement).closest('.planning-week-grid__event-menu, button')) return;

    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    // Close popover when starting drag
    setPopover(null);
    if (popoverTimerRef.current) {
      clearTimeout(popoverTimerRef.current);
      popoverTimerRef.current = null;
    }

    setDragState({
      event,
      startX: e.clientX,
      startY: e.clientY,
      isDragging: false,
      ghostX: e.clientX,
      ghostY: e.clientY,
      targetDayKey: null,
      targetHour: 0,
    });
  }, [onMoveEvent]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragState) return;

    const dx = e.clientX - dragState.startX;
    const dy = e.clientY - dragState.startY;
    const isDragging = dragState.isDragging || Math.abs(dx) + Math.abs(dy) > dragThreshold;

    if (!isDragging) return;

    // Find which column the mouse is over
    let targetDayKey: string | null = null;
    let targetHour = 0;

    columnsRef.current.forEach((el, key) => {
      const rect = el.getBoundingClientRect();
      if (e.clientX >= rect.left && e.clientX <= rect.right) {
        targetDayKey = key;
        const scrollTop = el.parentElement?.scrollTop || 0;
        const y = e.clientY - rect.top + scrollTop;
        targetHour = yToHour(y);
      }
    });

    setDragState(prev => prev ? {
      ...prev,
      isDragging: true,
      ghostX: e.clientX,
      ghostY: e.clientY,
      targetDayKey,
      targetHour,
    } : null);
  }, [dragState, yToHour]);

  const handlePointerUp = useCallback(() => {
    if (!dragState) return;

    if (dragState.isDragging && dragState.targetDayKey && onMoveEvent) {
      // Build new datetime
      const newDate = new Date(dragState.targetDayKey + 'T00:00:00');
      const hours = Math.floor(dragState.targetHour);
      const minutes = Math.round((dragState.targetHour - hours) * 60);
      newDate.setHours(hours, minutes, 0, 0);

      // Only move if datetime actually changed
      const oldDate = dragState.event.datetime ? new Date(dragState.event.datetime) : null;
      if (!oldDate || oldDate.getTime() !== newDate.getTime()) {
        onMoveEvent(dragState.event.id, newDate, dragState.event);
      }
    }

    if (dragState.isDragging) {
      justDraggedRef.current = true;
      setTimeout(() => { justDraggedRef.current = false; }, 100);
    }
    setDragState(null);
  }, [dragState, onMoveEvent]);

  // If we just finished dragging, block the click
  const handleEventClick = useCallback((event: Event) => {
    if (justDraggedRef.current) return;
    onEdit(event);
  }, [onEdit]);

  // === Resize handlers ===

  const handleResizePointerDown = useCallback((e: React.PointerEvent, event: Event, colKey: string) => {
    if (!onMoveEvent || event.status === 'cancelled') return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    // Close popover
    setPopover(null);
    if (popoverTimerRef.current) {
      clearTimeout(popoverTimerRef.current);
      popoverTimerRef.current = null;
    }

    // Calculate original end hour
    let origEndHour = 0;
    if (event.datetime) {
      const startH = getDecimalHour(new Date(event.datetime), event.timezone);
      origEndHour = startH + 1; // default 1h
      if (event.endTime) {
        origEndHour = parseEndTime(event.endTime, event.timezone);
        if (origEndHour <= startH) origEndHour = startH + 1;
      }
    }

    setResizeState({
      event,
      startY: e.clientY,
      isResizing: false,
      originalEndHour: origEndHour,
      newEndHour: origEndHour,
      columnKey: colKey,
    });
  }, [onMoveEvent]);

  const handleResizePointerMove = useCallback((e: React.PointerEvent) => {
    if (!resizeState) return;

    const dy = e.clientY - resizeState.startY;
    const isResizing = resizeState.isResizing || Math.abs(dy) > dragThreshold;
    if (!isResizing) return;

    // Calculate new end hour from mouse Y position
    const colEl = columnsRef.current.get(resizeState.columnKey);
    if (!colEl) return;

    const rect = colEl.getBoundingClientRect();
    const scrollTop = colEl.parentElement?.scrollTop || 0;
    const y = e.clientY - rect.top + scrollTop;
    let newEndHour = yToHour(y);

    // Minimum duration: 30min
    const startHour = resizeState.event.datetime ? getDecimalHour(new Date(resizeState.event.datetime), resizeState.event.timezone) : 0;
    if (newEndHour < startHour + 0.5) newEndHour = startHour + 0.5;

    setResizeState(prev => prev ? { ...prev, isResizing: true, newEndHour } : null);
  }, [resizeState, yToHour]);

  const handleResizePointerUp = useCallback(() => {
    if (!resizeState) return;

    if (resizeState.isResizing && onMoveEvent && resizeState.newEndHour !== resizeState.originalEndHour) {
      // Build new endTime as ISO
      const eventDate = resizeState.event.datetime ? new Date(resizeState.event.datetime) : new Date();
      const newEndH = Math.floor(resizeState.newEndHour);
      const newEndM = Math.round((resizeState.newEndHour - newEndH) * 60);
      const newEnd = new Date(eventDate);
      newEnd.setHours(newEndH, newEndM, 0, 0);

      // Call the same move handler but only change endTime
      const isRecurring = !!resizeState.event.seriesId;
      const updateData: Partial<Event> = { endTime: newEnd.toISOString() };
      if (isRecurring && !resizeState.event.isException) {
        updateData.isException = true;
      }

      const originalEndTime = resizeState.event.endTime;
      const wasException = resizeState.event.isException;
      const eventId = resizeState.event.id;
      const organizerId = resizeState.event.organizerId;

      supabaseEventService.updateEvent(eventId, updateData).then(() => {
        const durMin = Math.round((resizeState.newEndHour - getDecimalHour(new Date(resizeState.event.datetime!), resizeState.event.timezone)) * 60);
        const suffix = isRecurring ? ' · Only this week' : '';
        const durLabel = durMin >= 60 ? `${Math.floor(durMin/60)}h${durMin%60 > 0 ? durMin%60 : ''}` : `${durMin}min`;

        toast(
          (t) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Duration changed to {durLabel}{suffix}</span>
              <button
                onClick={async () => {
                  toast.dismiss(t.id);
                  try {
                    const undoData: Partial<Event> = {};
                    if (originalEndTime) undoData.endTime = originalEndTime;
                    if (isRecurring && !wasException) undoData.isException = false;
                    await supabaseEventService.updateEvent(eventId, undoData);
                    if (organizerId) smartListenerService.forceRefreshOrganizerEvents(organizerId);
                    toast.success('Resize undone');
                  } catch {
                    toast.error('Failed to undo');
                  }
                }}
                style={{
                  background: 'none',
                  border: '1px solid currentColor',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '12px',
                  color: 'inherit',
                  whiteSpace: 'nowrap',
                }}
              >
                Undo
              </button>
            </div>
          ),
          { duration: 6000, style: { maxWidth: '400px' } }
        );

        if (organizerId) smartListenerService.forceRefreshOrganizerEvents(organizerId);
      }).catch(() => {
        toast.error('Failed to resize event');
      });

      justDraggedRef.current = true;
      setTimeout(() => { justDraggedRef.current = false; }, 100);
    }

    setResizeState(null);
  }, [resizeState, onMoveEvent]);

  return (
    <div className="planning-week-grid">
      {/* Column headers */}
      <div className="planning-week-grid__headers">
        <div className="planning-week-grid__time-gutter-header" />
        {columns.map(col => (
          <div
            key={col.key}
            className={`planning-week-grid__header
              ${col.isToday ? 'planning-week-grid__header--today' : ''}
              ${col.isSelected ? 'planning-week-grid__header--selected' : ''}
            `}
          >
            <span className="planning-week-grid__header-day">{col.dayName}</span>
            <span className="planning-week-grid__header-num">{col.dayNum}</span>
          </div>
        ))}
      </div>

      {/* Scrollable container: buttons + grid body together */}
      <div className="planning-week-grid__scrollable" ref={bodyRef}>
        {/* "Show earlier hours" button */}
        {earlyHoursHidden && (
          <button
            className="planning-week-grid__expand-btn"
            onClick={() => setShowEarlyHours(true)}
            type="button"
          >
            <Icon name="ChevronUp" size={14} />
            <span>Show earlier hours ({formatTime(0)} – {formatTime(visibleStart)})</span>
          </button>
        )}

        {/* "Collapse early hours" button */}
        {showEarlyHours && visibleStart < smartStart && (
          <button
            className="planning-week-grid__expand-btn"
            onClick={() => setShowEarlyHours(false)}
            type="button"
          >
            <Icon name="ChevronDown" size={14} />
            <span>Hide hours before {formatTime(smartStart)}</span>
          </button>
        )}

        {/* Time grid body */}
        <div
          className={`planning-week-grid__body ${dragState?.isDragging ? 'planning-week-grid__body--dragging' : ''} ${resizeState?.isResizing ? 'planning-week-grid__body--resizing' : ''}`}
          onPointerMove={dragState ? handlePointerMove : resizeState ? handleResizePointerMove : undefined}
          onPointerUp={dragState ? handlePointerUp : resizeState ? handleResizePointerUp : undefined}
        >
          {/* Single LUNCH label spanning middle columns */}
          {hourOffsets[12] !== undefined && (
            <div
              className="planning-week-grid__lunch-label-overlay"
              style={{ top: hourOffsets[12], height: hourHeights[12] }}
            >
              LUNCH
            </div>
          )}

          {/* Hour labels gutter */}
          <div className="planning-week-grid__time-gutter" style={{ height: gridHeight }}>
            {hourLabels.map(h => (
              <div
                key={h}
                className="planning-week-grid__hour-label"
                style={{ top: hourOffsets[h], height: hourHeights[h] }}
              >
                {formatTime(h)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {columns.map(col => {
            const positioned = positionEvents(col.events);
            return (
              <div
                key={col.key}
                ref={(el) => { if (el) columnsRef.current.set(col.key, el); }}
                className={`planning-week-grid__column ${onCreateEventAt ? 'planning-week-grid__column--clickable' : ''}`}
                style={{ height: gridHeight }}
                onClick={(e) => handleColumnClick(e, col)}
                onMouseMove={onCreateEventAt ? (e) => handleColumnMouseMove(e, col) : undefined}
                onMouseLeave={onCreateEventAt ? handleColumnMouseLeave : undefined}
              >
                {/* Hover "+" hint — centered in the hovered hour slot */}
                {hoverHint && hoverHint.dayKey === col.key && !placeholder && (
                  <div
                    className="planning-week-grid__add-hint"
                    style={{ top: hoverHint.top, height: hoverHint.height }}
                  >
                    +
                  </div>
                )}

                {/* Click-to-create placeholder */}
                {placeholder && placeholder.dayKey === col.key && (
                  <div
                    className="planning-week-grid__placeholder"
                    style={{ top: placeholder.top, height: placeholder.height }}
                    onClick={(e) => { e.stopPropagation(); handlePlaceholderClick(); }}
                  >
                    <Icon name="Plus" size={14} />
                    <span>New event · {formatTime(placeholder.hour)}</span>
                    <button
                      className="planning-week-grid__placeholder-dismiss"
                      onClick={(e) => { e.stopPropagation(); handleDismissPlaceholder(); }}
                      type="button"
                      aria-label="Cancel"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                )}

                {/* Lunch zone background (12:00-13:00) */}
                {hourOffsets[12] !== undefined && (
                  <div
                    className="planning-week-grid__lunch-zone"
                    style={{ top: hourOffsets[12], height: hourHeights[12] }}
                  />
                )}

                {/* Hour grid lines */}
                {hourLabels.map(h => (
                  <div
                    key={h}
                    className={`planning-week-grid__hour-line ${h === 12 ? 'planning-week-grid__hour-line--lunch' : ''}`}
                    style={{ top: hourOffsets[h] }}
                  />
                ))}

                {/* Half-hour lines — only for hours with events */}
                {hourLabels.filter(h => hourHeights[h] === HOUR_HEIGHT).map(h => (
                  <div
                    key={`${h}-half`}
                    className="planning-week-grid__half-hour-line"
                    style={{ top: hourOffsets[h] + hourHeights[h] / 2 }}
                  />
                ))}

                {/* Events — stacked Apple Calendar style */}
                {positioned.map(({ event, top, height, layer, overlapCount, coveredHeight }) => {
                  const isRecurring = !!event.seriesId;
                  const isDraft = event.status === 'draft';
                  const isCancelled = event.status === 'cancelled';
                  const impressions = isRecurring && event.seriesId && seriesMetrics[event.seriesId]
                    ? seriesMetrics[event.seriesId].totalImpressions
                    : (eventMetrics[event.id]?.impressions ?? 0);

                  const hasOverlap = overlapCount > 1;
                  const isBackground = hasOverlap && layer === 0;

                  // Background (longest): peeks on the LEFT — inset from right
                  // Foreground (shortest): peeks on the RIGHT — inset from left
                  const leftOffset = isBackground ? 0 : layer * STACK_OFFSET;
                  const rightOffset = isBackground ? STACK_RIGHT_INSET : 0;

                  // Hovered event temporarily goes to front, resets on leave
                  const isHovered = topEventId === event.id;
                  const zIndex = isHovered ? 20 : 2 + layer;

                  // Compact: short events or background events covered by foreground
                  const visibleHeight = isBackground ? height - coveredHeight : height;
                  const isCompact = visibleHeight < 80;

                  return (
                    <div
                      key={event.id}
                      className={`planning-week-grid__event
                        ${isRecurring ? 'planning-week-grid__event--recurring' : 'planning-week-grid__event--single'}
                        ${isDraft ? 'planning-week-grid__event--draft' : ''}
                        ${isCancelled ? 'planning-week-grid__event--cancelled' : ''}
                        ${isHovered ? 'planning-week-grid__event--top' : ''}
                        ${hasOverlap && !isHovered ? 'planning-week-grid__event--stacked' : ''}
                        ${isCompact ? 'planning-week-grid__event--compact' : ''}
                        ${dragState?.isDragging && dragState.event.id === event.id ? 'planning-week-grid__event--dragging' : ''}
                      `}
                      style={{
                        top,
                        height: resizeState?.isResizing && resizeState.event.id === event.id
                          ? Math.max(hourToY(resizeState.newEndHour) - top, 28)
                          : Math.max(height, 28),
                        ['--event-height' as any]: `${Math.max(height, 28)}px`,
                        left: `${leftOffset + 3}px`,
                        right: `${rightOffset + 3}px`,
                        zIndex,
                      }}
                      onClick={() => handleEventClick(event)}
                      onPointerDown={onMoveEvent ? (e) => handleEventPointerDown(e, event) : undefined}
                      onMouseEnter={(e) => {
                        if (hasOverlap) setTopEventId(event.id);
                        handleEventMouseEnter(e, event, impressions);
                      }}
                      onMouseLeave={() => {
                        if (hasOverlap) setTopEventId(null);
                        handleEventMouseLeave();
                      }}
                    >
                      {/* Spacer to push content below overlapping foreground event */}
                      {isBackground && coveredHeight > 4 && (
                        <div className="planning-week-grid__event-spacer" style={{ minHeight: coveredHeight }} />
                      )}

                      {/* Top row: time range + draft chip + kebab menu */}
                      <div className="planning-week-grid__event-header">
                        <div className="planning-week-grid__event-time">
                          {formatTime(getDecimalHour(new Date(event.datetime!), event.timezone))}
                          {event.endTime && (
                            <span className="planning-week-grid__event-time-end">
                              {' - '}{event.endTime.includes('T') ? formatTime(parseEndTime(event.endTime, event.timezone)) : event.endTime}
                            </span>
                          )}
                        </div>
                        {isDraft && <span className="planning-week-grid__event-chip">DRAFT</span>}
                        {isRecurring && <Icon name="Repeat" size={10} className="planning-week-grid__event-repeat" />}
                        <div className="planning-week-grid__event-menu" onClick={e => e.stopPropagation()}>
                          <EventCardMenu
                            eventId={event.id}
                            eventStatus={event.status}
                            seriesId={event.seriesId}
                            activeTab={getActiveTab(event)}
                            isSeriesMenu={isRecurring}
                            onEdit={() => onEdit(event)}
                            onDuplicate={() => {
                              const { id, ...rest } = event;
                              onDuplicate({ ...rest, status: 'draft' } as Event);
                            }}
                            onAddOccurrence={isRecurring && onAddOccurrence ? () => onAddOccurrence(event) : undefined}
                            onCancel={onCancel ? () => onCancel(event) : undefined}
                            onRestore={onRestore ? () => onRestore(event) : undefined}
                            onPublish={onPublish ? () => onPublish(event) : undefined}
                            onDelete={onDelete ? () => onDelete(event) : undefined}
                            onInstagram={onInstagram ? () => onInstagram(event) : undefined}
                          />
                        </div>
                      </div>

                      {/* Title */}
                      <div className="planning-week-grid__event-title">
                        {event.title || 'Untitled'}
                      </div>

                      {/* Hover-expand details: image, location, recurrence, description */}
                      <div className="planning-week-grid__event-details">
                        {event.image && (
                          <div className="planning-week-grid__event-image">
                            <img
                              src={event.image.replace('/upload/', '/upload/f_auto,q_auto,w_160,h_90,c_fill/')}
                              alt=""
                              loading="lazy"
                              draggable={false}
                            />
                          </div>
                        )}
                        {(event.locationName || event.location) && (
                          <div className="planning-week-grid__event-location">
                            <Icon name="MapPin" size={11} />
                            <span>{event.locationName || event.location}</span>
                          </div>
                        )}
                        {isRecurring && event.weekDays && event.weekDays.length > 0 && (
                          <div className="planning-week-grid__event-recurrence">
                            <Icon name="Repeat" size={11} />
                            <span>Every {event.weekDays.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ')}</span>
                          </div>
                        )}
                        {event.description && (
                          <div className="planning-week-grid__event-description">
                            {event.description.length > 80 ? event.description.slice(0, 80) + '...' : event.description}
                          </div>
                        )}
                      </div>

                      {/* Bottom row: impressions left, draft chip or instagram right */}
                      <div className="planning-week-grid__event-footer">
                        <div className="planning-week-grid__event-meta">
                          <Icon name="Eye" size={13} />
                          <span>{impressions}</span>
                        </div>
                        {isDraft && onPublish && (
                          <button
                            className="planning-week-grid__event-publish"
                            onClick={(e) => {
                              e.stopPropagation();
                              onPublish(event);
                            }}
                            type="button"
                            title="Publish event"
                          >
                            <Icon name="Send" size={12} />
                            <span>Publish</span>
                          </button>
                        )}
                        {!isDraft && event.status === 'active' && onInstagram && (
                          <button
                            className="planning-week-grid__event-instagram"
                            onClick={(e) => {
                              e.stopPropagation();
                              // For recurring events, pass the first occurrence or series representative
                              onInstagram(event);
                            }}
                            type="button"
                            title="Generate Instagram post"
                          >
                            <Icon name="Instagram" size={14} />
                          </button>
                        )}
                      </div>

                      {/* Resize handle — bottom edge */}
                      {onMoveEvent && event.status !== 'cancelled' && (
                        <div
                          className="planning-week-grid__event-resize-handle"
                          onPointerDown={(e) => handleResizePointerDown(e, event, col.key)}
                        />
                      )}

                      {/* Resize label — shows new end time during resize */}
                      {resizeState?.isResizing && resizeState.event.id === event.id && (
                        <div className="planning-week-grid__event-resize-label">
                          {formatTime(resizeState.newEndHour)}
                          {' '}({(() => {
                            const startH = event.datetime ? getDecimalHour(new Date(event.datetime), event.timezone) : 0;
                            const durMin = Math.round((resizeState.newEndHour - startH) * 60);
                            return durMin >= 60 ? `${Math.floor(durMin/60)}h${durMin%60 > 0 ? durMin%60 : ''}` : `${durMin}min`;
                          })()})
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* "Show later hours" button */}
        {lateHoursHidden && (
          <button
            className="planning-week-grid__expand-btn"
            onClick={() => setShowLateHours(true)}
            type="button"
          >
            <Icon name="ChevronDown" size={14} />
            <span>Show later hours ({formatTime(visibleEnd)} – {formatTime(24)})</span>
          </button>
        )}

        {/* "Collapse late hours" button */}
        {showLateHours && visibleEnd > smartEnd && (
          <button
            className="planning-week-grid__expand-btn"
            onClick={() => setShowLateHours(false)}
            type="button"
          >
            <Icon name="ChevronUp" size={14} />
            <span>Hide hours after {formatTime(smartEnd)}</span>
          </button>
        )}
      </div>

      {/* Popover — lateral preview on hover */}
      {popover && !dragState?.isDragging && (
        <div
          className={`planning-week-grid__popover ${popover.event.seriesId ? 'planning-week-grid__popover--recurring' : 'planning-week-grid__popover--single'}`}
          style={{
            left: Math.min(popover.x, window.innerWidth - 280),
            top: Math.max(popover.y, 8),
          }}
        >
          {popover.event.image && (
            <div className="planning-week-grid__popover-image">
              <img
                src={popover.event.image.replace('/upload/', '/upload/f_auto,q_auto,w_260,h_100,c_fill/')}
                alt=""
              />
            </div>
          )}
          <div className="planning-week-grid__popover-title">
            {popover.event.title || 'Untitled'}
          </div>
          <div className="planning-week-grid__popover-time">
            {popover.event.datetime && formatTime(getDecimalHour(new Date(popover.event.datetime), popover.event.timezone))}
            {popover.event.endTime && (
              <span> – {popover.event.endTime.includes('T') ? formatTime(parseEndTime(popover.event.endTime, popover.event.timezone)) : popover.event.endTime}</span>
            )}
          </div>
          <div className="planning-week-grid__popover-meta">
            {(popover.event.locationName || popover.event.location) && (
              <div className="planning-week-grid__popover-meta-item">
                <Icon name="MapPin" size={13} />
                <span>{popover.event.locationName || popover.event.location}</span>
              </div>
            )}
            {popover.event.seriesId && popover.event.weekDays && popover.event.weekDays.length > 0 && (
              <div className="planning-week-grid__popover-meta-item planning-week-grid__popover-recurrence">
                <Icon name="Repeat" size={13} />
                <span>Every {popover.event.weekDays.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ')}</span>
              </div>
            )}
          </div>
          {popover.event.description && (
            <div className="planning-week-grid__popover-description">
              {popover.event.description}
            </div>
          )}
          <div className="planning-week-grid__popover-footer">
            <div className="planning-week-grid__popover-views">
              <Icon name="Eye" size={13} />
              <span>{popover.impressions} {popover.impressions === 1 ? 'view' : 'views'}</span>
            </div>
            {popover.event.status === 'draft' && (
              <span className="planning-week-grid__popover-chip">DRAFT</span>
            )}
          </div>
        </div>
      )}

      {/* Drag ghost — fixed overlay following the mouse */}
      {dragState?.isDragging && (
        <div
          className={`planning-week-grid__drag-ghost ${dragState.event.seriesId ? 'planning-week-grid__drag-ghost--recurring' : 'planning-week-grid__drag-ghost--single'}`}
          style={{
            left: dragState.ghostX - 60,
            top: dragState.ghostY - 16,
          }}
        >
          <span className="planning-week-grid__drag-ghost-time">
            {formatTime(dragState.targetHour)}
          </span>
          <span className="planning-week-grid__drag-ghost-title">
            {dragState.event.title || 'Untitled'}
          </span>
        </div>
      )}
    </div>
  );
};

export default React.memo(PlanningWeekGrid);
